<!-- This component is used on the complete step to display a success message for submitted plans and a button to preview the plan. -->
<template>
	<div
		class="border-primary-50 mx-5 my-6 flex flex-col items-center gap-4 rounded-md border bg-slate-50 px-5 py-3"
	>
		<PlanAvatar
			:plan="planHolder"
			:index="index"
			:filled="true"
			:large="true"
			class="font-base! -translate-y-8 scale-[1.8] transform text-lg"
		/>
		<Menu
			v-if="planHolder.packetStatus === PlanPacketStatus.ManuallyResolved"
			as="div"
			class="fixed inline-block self-end pt-2 text-left"
		>
			<MenuButton class="h-full"
				><EllipsisVerticalIcon class="ml-2 h-7 w-7"
			/></MenuButton>
			<transition
				enter-active-class="transition duration-100 ease-out"
				enter-from-class="transform scale-95 opacity-0"
				enter-to-class="transform scale-100 opacity-100"
				leave-active-class="transition duration-75 ease-in"
				leave-from-class="transform scale-100 opacity-100"
				leave-to-class="transform scale-95 opacity-0"
			>
				<MenuItems
					as="section"
					class="text-secondary-800 absolute right-0 flex w-36 flex-col rounded-lg border border-slate-100 bg-white font-light shadow-md"
				>
					<MenuItem
						as="component"
						class="text-md rounded-t-lg p-2 text-center hover:cursor-pointer hover:bg-slate-100"
						@click="submissionStore.submitPlanPacket(planHolder)"
					>
						{{ t('document.submission.resubmitPlan') }}
					</MenuItem>
				</MenuItems>
			</transition>
		</Menu>
		<div
			class="text-primary-700 flex flex-col items-center gap-2 text-center font-serif text-5xl tracking-tight"
		>
			<p class="font-thin">
				{{ t('document.congratulationsTitle', { name: planHolder.firstName }) }}
			</p>
			<p>
				{{ t('document.congratulationsText') }}
			</p>
		</div>
		<button
			type="button"
			class="text-primary-400 disabled:text-secondary-400 flex items-center gap-2 rounded-md p-2 text-xs font-bold disabled:cursor-not-allowed"
			:disabled="!foxitReady"
			@click="$emit('preview')"
		>
			<DocumentTextIcon class="h-5" />
			{{ t('document.yourDocuments') }}
		</button>
		<div
			class="text-secondary-600 mt-4 mb-10 flex max-w-sm flex-col gap-4 text-sm *:flex *:items-center *:gap-4"
		>
			<div v-if="planHolder.email">
				<EnvelopeIcon class="text-primary-700 size-6 shrink-0" />
				<span>{{ t('document.nextSteps.summaryEmail') }}</span>
			</div>

			<div>
				<ClipboardDocumentIcon class="text-primary-700 size-6 shrink-0" />
				<span>{{ t('document.nextSteps.policyPacket') }}</span>
			</div>

			<div v-if="planHolder.email">
				<EllipsisHorizontalCircleIcon
					class="text-primary-700 size-6 shrink-0"
				/>
				<span>
					{{
						planHolder.everplansIncluded
							? t('document.nextSteps.everplansLifetimeEmail')
							: t('document.nextSteps.everplansEmail')
					}}
				</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { usePresentationLocale } from '@/i18n'
import { useSubmissionStore } from '@/stores/submission'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { DocumentTextIcon } from '@heroicons/vue/24/outline'
import {
	EllipsisVerticalIcon,
	EnvelopeIcon,
	ClipboardDocumentIcon,
	EllipsisHorizontalCircleIcon,
} from '@heroicons/vue/24/solid'

const submissionStore = useSubmissionStore()

defineProps<{
	planHolder: Plan
	index: number
	foxitReady: boolean
}>()

const { t } = usePresentationLocale()

defineEmits<{
	preview: []
}>()
</script>
